
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../i18n/useTranslation';
import { UserProfile, Generation } from '../types';
import { CheckCircleIcon, ArrowRightIcon, ArrowLeftIcon, SparklesIcon, BriefcaseIcon, MapIcon, SunIcon } from './icons';

interface StyleWizardProps {
    userProfile: UserProfile;
    setUserProfile: (profile: UserProfile | ((prev: UserProfile) => UserProfile)) => void;
    onComplete: () => void;
}

const StyleWizard: React.FC<StyleWizardProps> = ({ userProfile, setUserProfile, onComplete }) => {
    const { t } = useTranslation();
    const [step, setStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const totalSteps = 6;

    const handleNext = () => setStep(prev => Math.min(prev + 1, totalSteps));
    const handleBack = () => setStep(prev => Math.max(prev - 1, 1));

    const updateProfile = (updates: Partial<UserProfile>) => {
        setUserProfile(prev => ({
            ...prev,
            ...updates
        }));
    };

    const updateLifestyle = (key: keyof NonNullable<UserProfile['lifestyle']>, value: any) => {
        setUserProfile(prev => ({
            ...prev,
            lifestyle: {
                ...(prev.lifestyle || {}),
                [key]: value
            }
        }));
    };

    const handleFinish = async () => {
        setIsSubmitting(true);
        // Simulate a small saving delay for effect
        await new Promise(r => setTimeout(r, 1000));
        setIsSubmitting(false);
        setShowSuccess(true);
        setTimeout(() => {
            onComplete();
        }, 3000);
    };

    if (showSuccess) {
        return (
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center p-12 text-center h-full"
            >
                <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-full flex items-center justify-center mb-6">
                    <CheckCircleIcon className="w-12 h-12" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    {t('stylingAdvisorPage_questionnaire_wizard_success')}
                </h2>
                <div className="flex gap-2">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce delay-100"></div>
                    <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce delay-200"></div>
                </div>
            </motion.div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-xl overflow-hidden">
            {/* Header / Progress */}
            <header className="p-6 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-serif font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <SparklesIcon className="w-5 h-5 text-indigo-500" />
                        {t('stylingAdvisorPage_questionnaire_wizard_title')}
                    </h2>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        {step} / {totalSteps}
                    </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${(step / totalSteps) * 100}%` }}
                        className="bg-indigo-600 h-full"
                    />
                </div>
            </header>

            {/* Step Content */}
            <main className="flex-1 overflow-y-auto p-8 custom-scrollbar">
                <AnimatePresence mode="wait">
                    {step === 1 && (
                        <motion.div
                            key="step1"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">
                                {t('stylingAdvisorPage_questionnaire_wizard_step_evolution') || 'What phase of inner development are you currently reflecting?'}
                            </h3>
                            <div className="flex flex-col gap-4">
                                {(['EMERGENCE', 'LEADERSHIP', 'RECLAMATION', 'TRANSITION', 'MASTERY'] as any[]).map(phase => (
                                    <button
                                        key={phase}
                                        onClick={() => updateProfile({ innerDevelopmentPhase: phase })}
                                        className={`p-4 rounded-xl border-2 text-left transition-all ${userProfile.innerDevelopmentPhase === phase
                                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-500'
                                            : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                                            }`}
                                    >
                                        <div className="font-bold text-gray-900 dark:text-white capitalize">
                                            {t(`stylingAdvisorPage_questionnaire_wizard_phase_${phase.toLowerCase()}` as any) || phase}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {step === 2 && (
                        <motion.div
                            key="step2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">
                                {t('stylingAdvisorPage_questionnaire_wizard_step_archetype') || 'What is your current style Archetype?'}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {(['MUSE', 'WARRIOR', 'CREATOR', 'HEALER', 'RULER', 'MAGICIAN'] as any[]).map(arch => (
                                    <button
                                        key={arch}
                                        onClick={() => updateProfile({ archetype: arch })}
                                        className={`p-4 rounded-xl border-2 text-center transition-all ${userProfile.archetype === arch
                                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-500'
                                            : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                                            }`}
                                    >
                                        <div className="font-bold text-gray-900 dark:text-white capitalize">
                                            {t(`stylingAdvisorPage_questionnaire_wizard_arch_${arch.toLowerCase()}` as any) || arch}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {step === 3 && (
                        <motion.div
                            key="step1"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">
                                {t('stylingAdvisorPage_questionnaire_wizard_step_generation')}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {(['alpha', 'z', 'millennial', 'x', 'boomer', 'classic'] as Generation[]).map(gen => (
                                    <button
                                        key={gen}
                                        onClick={() => updateProfile({ generation: gen })}
                                        className={`p-4 rounded-xl border-2 text-left transition-all ${userProfile.generation === gen
                                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-500'
                                            : 'border-gray-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                                            }`}
                                    >
                                        <div className="font-bold text-gray-900 dark:text-white capitalize">
                                            {t(`stylingAdvisorPage_questionnaire_wizard_gen_${gen}` as any)}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    {step === 4 && (
                        <motion.div
                            key="step2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-8"
                        >
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">
                                {t('stylingAdvisorPage_questionnaire_wizard_step_lifestyle')}
                            </h3>

                            <div className="space-y-6">
                                {/* Office Time */}
                                <div className="space-y-3">
                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <BriefcaseIcon className="w-4 h-4 text-indigo-500" />
                                        {t('stylingAdvisorPage_questionnaire_wizard_lifestyle_office')}
                                    </label>
                                    <div className="flex gap-2">
                                        {(['none', 'low', 'medium', 'high'] as const).map(level => (
                                            <button
                                                key={level}
                                                onClick={() => updateLifestyle('officeTime', level)}
                                                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${userProfile.lifestyle?.officeTime === level
                                                    ? 'bg-indigo-600 text-white shadow-md'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                    }`}
                                            >
                                                {t(`stylingAdvisorPage_questionnaire_wizard_intensity_${level}` as any)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Outdoor Time */}
                                <div className="space-y-3">
                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <SunIcon className="w-4 h-4 text-amber-500" />
                                        {t('stylingAdvisorPage_questionnaire_wizard_lifestyle_outdoor')}
                                    </label>
                                    <div className="flex gap-2">
                                        {(['none', 'low', 'medium', 'high'] as const).map(level => (
                                            <button
                                                key={level}
                                                onClick={() => updateLifestyle('outdoorTime', level)}
                                                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${userProfile.lifestyle?.outdoorTime === level
                                                    ? 'bg-indigo-600 text-white shadow-md'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                    }`}
                                            >
                                                {t(`stylingAdvisorPage_questionnaire_wizard_intensity_${level}` as any)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Travel Time */}
                                <div className="space-y-3">
                                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                        <MapIcon className="w-4 h-4 text-blue-500" />
                                        {t('stylingAdvisorPage_questionnaire_wizard_lifestyle_travel')}
                                    </label>
                                    <div className="flex gap-2">
                                        {(['none', 'low', 'medium', 'high'] as const).map(level => (
                                            <button
                                                key={level}
                                                onClick={() => updateLifestyle('travelTime', level)}
                                                className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${userProfile.lifestyle?.travelTime === level
                                                    ? 'bg-indigo-600 text-white shadow-md'
                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                    }`}
                                            >
                                                {t(`stylingAdvisorPage_questionnaire_wizard_intensity_${level}` as any)}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {step === 5 && (
                        <motion.div
                            key="step3"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">
                                {t('stylingAdvisorPage_questionnaire_wizard_step_body')}
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="bodySize" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('profilePage_attributes_bodySize')}</label>
                                    <select
                                        id="bodySize"
                                        value={userProfile.bodySize || ''}
                                        onChange={(e) => updateProfile({ bodySize: e.target.value })}
                                        className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl"
                                        title={t('profilePage_attributes_bodySize') || 'Body Size'}
                                    >
                                        <option value="">Select...</option>
                                        <option value="Hourglass">Hourglass</option>
                                        <option value="Pear">Pear</option>
                                        <option value="Rectangle">Rectangle</option>
                                        <option value="Inverted Triangle">Inverted Triangle</option>
                                        <option value="Oval">Oval</option>
                                        <option value="Athletic">Athletic</option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="faceShape" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('profilePage_attributes_faceShape')}</label>
                                    <select
                                        id="faceShape"
                                        value={userProfile.faceShape || ''}
                                        onChange={(e) => updateProfile({ faceShape: e.target.value })}
                                        className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl"
                                        title={t('profilePage_attributes_faceShape') || 'Face Shape'}
                                    >
                                        <option value="">Select...</option>
                                        <option value="Oval">Oval</option>
                                        <option value="Round">Round</option>
                                        <option value="Square">Square</option>
                                        <option value="Heart">Heart</option>
                                        <option value="Long">Long</option>
                                        <option value="Diamond">Diamond</option>
                                    </select>
                                </div>
                                <div>
                                    <label htmlFor="colorSeason" className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{t('profilePage_measurements_colorSeason_label')}</label>
                                    <select
                                        id="colorSeason"
                                        value={userProfile.colorSeason || ''}
                                        onChange={(e) => updateProfile({ colorSeason: e.target.value })}
                                        className="w-full p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl"
                                        title={t('profilePage_measurements_colorSeason_label') || 'Color Season'}
                                    >
                                        <option value="">Select...</option>
                                        <option value="Clear Winter">Clear Winter</option>
                                        <option value="Cool Winter">Cool Winter</option>
                                        <option value="Deep Winter">Deep Winter</option>
                                        <option value="Light Spring">Light Spring</option>
                                        <option value="Clear Spring">Clear Spring</option>
                                        <option value="Warm Spring">Warm Spring</option>
                                        <option value="Soft Summer">Soft Summer</option>
                                        <option value="Light Summer">Light Summer</option>
                                        <option value="Cool Summer">Cool Summer</option>
                                        <option value="Deep Autumn">Deep Autumn</option>
                                        <option value="Soft Autumn">Soft Autumn</option>
                                        <option value="Warm Autumn">Warm Autumn</option>
                                    </select>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {step === 6 && (
                        <motion.div
                            key="step4"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            className="space-y-6"
                        >
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4">
                                {t('stylingAdvisorPage_questionnaire_wizard_step_persona')}
                            </h3>

                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    'minimalist', 'bohoChic', 'classicProfessional', 'streetwear',
                                    'avantGarde', 'vintageRetro', 'activewear', 'glamorous'
                                ].map(personaKey => (
                                    <button
                                        key={personaKey}
                                        onClick={() => updateProfile({ stylePersona: personaKey })}
                                        className={`p-4 rounded-xl border-2 text-center transition-all ${userProfile.stylePersona === personaKey
                                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-500'
                                            : 'border-gray-100 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-700'
                                            }`}
                                    >
                                        <span className="text-sm font-semibold">
                                            {t(`stylingAdvisorPage_questionnaire_wizard_persona_${personaKey}` as any)}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>

            {/* Footer Navigation */}
            <footer className="p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex justify-between items-center">
                <button
                    onClick={handleBack}
                    disabled={step === 1 || isSubmitting}
                    className="flex items-center gap-2 px-6 py-2 rounded-lg font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-30"
                >
                    <ArrowLeftIcon className="w-4 h-4" />
                    {t('common_back' as any) || 'Back'}
                </button>

                {step < totalSteps ? (
                    <button
                        onClick={handleNext}
                        className="flex items-center gap-2 px-8 py-3 rounded-xl font-bold bg-indigo-600 text-white hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 dark:shadow-none"
                    >
                        {t('common_next')}
                        <ArrowRightIcon className="w-4 h-4" />
                    </button>
                ) : (
                    <button
                        onClick={handleFinish}
                        disabled={isSubmitting}
                        className="flex items-center gap-2 px-8 py-3 rounded-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:opacity-90 transition-all shadow-lg"
                    >
                        {isSubmitting ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <SparklesIcon className="w-4 h-4" />
                        )}
                        {t('stylingAdvisorPage_questionnaire_wizard_finish')}
                    </button>
                )}
            </footer>
        </div >
    );
};

export default StyleWizard;
